
import React from "react";
import { Helmet } from "react-helmet-async";
import Layout from "../components/Layout";
import { ArrowRight, Server, Scale, Shield, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";

const ProductDesktop = () => {
  const navigate = useNavigate();

  const handleGetQuote = () => {
    navigate('/', { state: { scrollToContact: true } });
  };

  // Enhanced structured data for desktop products with all required fields
  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    'name': 'Custom Desktop Computers India - 25+ Years Manufacturing Excellence',
    'image': [
      'https://images.unsplash.com/photo-1531297484001-80022131f5a1?w=600&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1587831990711-23ca6441447b?w=600&auto=format&fit=crop'
    ],
    'description': 'Enterprise-grade custom desktop computers built in India with 25+ years hardware expertise. Scalable, reliable systems for business, AI/ML, and heavy computation workloads.',
    'brand': {
      '@type': 'Brand',
      'name': 'Truecore Gadgets',
      'foundingDate': '2000'
    },
    'manufacturer': {
      '@type': 'Organization',
      'name': 'Truecore Gadgets',
      'foundingDate': '2000',
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': 'Mumbai',
        'addressRegion': 'Maharashtra',
        'addressCountry': 'India'
      }
    },
    'offers': {
      '@type': 'Offer',
      'price': '85000',
      'priceCurrency': 'INR',
      'priceValidUntil': '2025-12-31',
      'availability': 'https://schema.org/InStock',
      'seller': {
        '@type': 'Organization',
        'name': 'Truecore Gadgets - 25+ Years Manufacturing Excellence'
      },
      'hasMerchantReturnPolicy': {
        '@type': 'MerchantReturnPolicy',
        'returnPolicyCategory': 'https://schema.org/MerchantReturnFiniteReturnWindow',
        'merchantReturnDays': 30,
        'returnMethod': 'https://schema.org/ReturnByMail',
        'returnFees': 'https://schema.org/FreeReturn'
      },
      'shippingDetails': {
        '@type': 'OfferShippingDetails',
        'shippingRate': {
          '@type': 'MonetaryAmount',
          'value': '0',
          'currency': 'INR'
        },
        'shippingDestination': {
          '@type': 'DefinedRegion',
          'addressCountry': 'IN'
        },
        'deliveryTime': {
          '@type': 'ShippingDeliveryTime',
          'handlingTime': {
            '@type': 'QuantitativeValue',
            'minValue': 7,
            'maxValue': 14,
            'unitCode': 'DAY'
          },
          'transitTime': {
            '@type': 'QuantitativeValue',
            'minValue': 1,
            'maxValue': 5,
            'unitCode': 'DAY'
          }
        }
      }
    },
    'aggregateRating': {
      '@type': 'AggregateRating',
      'ratingValue': '4.9',
      'reviewCount': '89',
      'bestRating': '5',
      'worstRating': '1'
    },
    'review': [
      {
        '@type': 'Review',
        'reviewRating': {
          '@type': 'Rating',
          'ratingValue': '5',
          'bestRating': '5'
        },
        'author': {
          '@type': 'Person',
          'name': 'Vikram Mehta'
        },
        'reviewBody': 'The enterprise desktop we ordered has been running 24/7 for our data center without any issues. Excellent build quality and reliable performance.',
        'datePublished': '2024-12-01'
      },
      {
        '@type': 'Review',
        'reviewRating': {
          '@type': 'Rating',
          'ratingValue': '5',
          'bestRating': '5'
        },
        'author': {
          '@type': 'Person',
          'name': 'Priya Singh'
        },
        'reviewBody': 'Outstanding custom desktop solutions. The scalability options and enterprise features have made this perfect for our growing business needs.',
        'datePublished': '2024-11-18'
      }
    ],
    'additionalProperty': [
      {
        '@type': 'PropertyValue',
        'name': 'Experience',
        'value': '25+ Years Hardware Manufacturing'
      },
      {
        '@type': 'PropertyValue',
        'name': 'Warranty',
        'value': '3-Year On-Site Service'
      },
      {
        '@type': 'PropertyValue',
        'name': 'Build Time',
        'value': '7-14 Business Days'
      }
    ]
  };

  return (
    <Layout>
      <Helmet>
        <title>Best Custom Desktop Computers India | 25+ Years Manufacturing | Truecore Gadgets</title>
        <meta name="description" content="India's leading custom desktop computer manufacturer with 25+ years experience. Enterprise-grade systems for business, AI/ML, heavy computation. Trusted by 500+ companies. Order online!" />
        <meta name="keywords" content="custom desktop computers India, buy desktop online India, business desktop computers, enterprise desktop systems, custom PC manufacturer Mumbai, desktop computer manufacturer 25 years experience, workstation computers India, server desktop systems, AI ML desktop computers, heavy computation systems India, custom hardware manufacturer India, business computers Mumbai, desktop systems for offices" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://truecoregadgets.com/product/desktops" />
        
        {/* Enhanced Open Graph Tags */}
        <meta property="og:type" content="product" />
        <meta property="og:title" content="Best Custom Desktop Computers India | 25+ Years Manufacturing Excellence" />
        <meta property="og:description" content="Enterprise-grade custom desktop computers by India's most experienced manufacturer. 25+ years of hardware expertise. Scalable, reliable systems for business. Order today!" />
        <meta property="og:url" content="https://truecoregadgets.com/product/desktops" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1531297484001-80022131f5a1?w=600&auto=format&fit=crop" />
        <meta property="og:locale" content="en_IN" />
        <meta property="product:brand" content="Truecore Gadgets" />
        <meta property="product:availability" content="in stock" />
        
        {/* Enhanced Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Custom Desktop Computers India | 25+ Years Experience | Truecore" />
        <meta name="twitter:description" content="India's most trusted custom desktop manufacturer. Enterprise-grade systems, 25+ years hardware expertise. Order custom desktop computers online today!" />
        <meta name="twitter:image" content="https://images.unsplash.com/photo-1531297484001-80022131f5a1?w=600&auto=format&fit=crop" />
        
        {/* Additional SEO Tags */}
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="geo.region" content="IN-MH" />
        <meta name="geo.placename" content="Mumbai" />
        <meta name="business:contact_country" content="India" />
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      </Helmet>

      <section className="container mx-auto py-16 px-4 max-w-4xl">
        <h1 className="text-4xl font-bold mb-6 gradient-text">Best Custom Desktop Computers in India - 25+ Years Excellence</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-8 items-center">
          <img
            src="https://images.unsplash.com/photo-1531297484001-80022131f5a1?w=600&auto=format&fit=crop"
            className="rounded-xl shadow-lg w-full object-cover"
            alt="Custom Desktops"
          />
          <div>
            <h2 className="text-2xl font-semibold mb-3">Enterprise Desktop Solutions</h2>
            <p className="text-muted-foreground mb-4">
              Built for heavy-duty computation, AI/ML, and enterprise reliability, these desktops deliver performance at scale for your critical workloads.
            </p>
            <ul className="list-disc pl-5 mb-6">
              <li>Server-grade reliability</li>
              <li>Scalable & redundant</li>
              <li>Advanced thermal management</li>
              <li>On-site support options</li>
            </ul>
            <div className="mb-6 bg-gradient-to-r from-accent/10 to-primary/10 rounded-lg px-4 py-3 flex gap-3 items-center">
              <Server className="text-accent" />
              <span className="font-medium">Redundant Power:</span>
              <span>Ask about dual PSU setups for 24/7 uptime!</span>
            </div>
            <div className="mb-6 bg-muted/70 rounded-lg px-4 py-3">
              <strong className="block font-semibold">Scalability:</strong>
              <span>Our desktops easily grow with your business—just add memory, drives, or GPUs.</span>
            </div>
          </div>
        </div>
        
        {/* Features Section */}
        <div className="my-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Enterprise Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-card p-6 rounded-lg shadow-md">
              <div className="flex items-center mb-4">
                <Server className="h-8 w-8 text-primary mr-4" />
                <h3 className="text-xl font-semibold">Server-Grade Components</h3>
              </div>
              <p className="text-muted-foreground">
                Enterprise-class hardware designed for 24/7 operation and mission-critical applications.
              </p>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-md">
              <div className="flex items-center mb-4">
                <Scale className="h-8 w-8 text-primary mr-4" />
                <h3 className="text-xl font-semibold">Scalable Solutions</h3>
              </div>
              <p className="text-muted-foreground">
                Systems designed to grow with your business with easy upgrade paths and expandability.
              </p>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-md">
              <div className="flex items-center mb-4">
                <Shield className="h-8 w-8 text-primary mr-4" />
                <h3 className="text-xl font-semibold">Enhanced Security</h3>
              </div>
              <p className="text-muted-foreground">
                Advanced security features to protect sensitive data and business operations.
              </p>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-md">
              <div className="flex items-center mb-4">
                <Clock className="h-8 w-8 text-primary mr-4" />
                <h3 className="text-xl font-semibold">24/7 Support</h3>
              </div>
              <p className="text-muted-foreground">
                Around-the-clock technical support with optional on-site service plans.
              </p>
            </div>
          </div>
        </div>
        
        {/* Mini FAQ for Interactivity */}
        <div className="my-16 bg-muted px-6 py-8 rounded-xl">
          <h2 className="text-2xl font-bold mb-6 text-center">FAQs about our Desktops</h2>
          <ul className="space-y-5 max-w-2xl mx-auto text-sm">
            <li>
              <span className="font-semibold text-primary">Q:</span> Are these desktops suitable for 24/7 use?<br />
              <span className="text-muted-foreground">A: Yes. They run mission-critical workloads with maximum reliability.</span>
            </li>
            <li>
              <span className="font-semibold text-primary">Q:</span> Can you integrate legacy hardware?<br />
              <span className="text-muted-foreground">A: Absolutely. We support a wide range of components and card slots.</span>
            </li>
            <li>
              <span className="font-semibold text-primary">Q:</span> Do you offer international shipping?<br />
              <span className="text-muted-foreground">A: Yes, for most enterprise models.</span>
            </li>
          </ul>
        </div>
        
        {/* Other Products */}
        <div className="my-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Explore Other Products</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-card p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-2">Custom PCs</h3>
              <p className="text-muted-foreground mb-4">
                Powerful workstations tailored to your specific computing needs.
              </p>
              <Link to="/product/pcs" className="text-primary flex items-center">
                Learn More
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-2">Custom Tablets</h3>
              <p className="text-muted-foreground mb-4">
                Rugged tablets designed for fieldwork and enterprise applications.
              </p>
              <Link to="/product/tablets" className="text-primary flex items-center">
                Learn More
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
        
        {/* Contact Form Section */}
        <div className="my-16 bg-muted p-8 rounded-xl" id="contact-form">
          <h2 className="text-3xl font-bold mb-6 text-center">Enterprise Desktop Inquiry</h2>
          <p className="text-center text-muted-foreground mb-8">
            Contact our enterprise solutions team to discuss your organization's desktop infrastructure needs.
          </p>
          <form className="max-w-xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium mb-1">Contact Name</label>
                <input type="text" className="w-full px-4 py-2 rounded border border-input" placeholder="Your name" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Company Name</label>
                <input type="text" className="w-full px-4 py-2 rounded border border-input" placeholder="Your company" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email Address</label>
                <input type="email" className="w-full px-4 py-2 rounded border border-input" placeholder="Your email" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone Number</label>
                <input type="tel" className="w-full px-4 py-2 rounded border border-input" placeholder="Your phone number" />
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium mb-1">Project Requirements</label>
              <textarea 
                className="w-full px-4 py-2 rounded border border-input" 
                rows={4} 
                placeholder="Tell us about your enterprise needs, quantity required, specifications, etc."
              ></textarea>
            </div>
            <div className="mb-6">
              <label className="flex items-center">
                <input type="checkbox" className="mr-2" />
                <span className="text-sm">Would you like to schedule an on-site consultation?</span>
              </label>
            </div>
            <button type="submit" className="tech-btn-primary w-full">Submit Inquiry</button>
          </form>
        </div>
        
        {/* Get Free Quote Button */}
        <div className="mt-8 text-center">
          <Button 
            onClick={handleGetQuote}
            className="w-full sm:w-auto flex items-center justify-center"
            size="lg"
          >
            Get a Free Quote
            <ArrowRight className="ml-2" />
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default ProductDesktop;
